import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, Button } from '../ui';

/**
 * CustomerTreatmentRecords component displays a customer's treatment history
 */
const CustomerTreatmentRecords = ({ customerId, treatmentRecords = [], loading = false, onCreateRecord = () => { } }) => {
  const { hasPermission } = useAuth();
  const canCreateRecord = hasPermission('treatmentRecords', 'write');

  // Sort treatment records by date (newest first)
  const sortedRecords = useMemo(() => {
    return [...treatmentRecords].sort((a, b) => {
      const dateA = new Date(a.treatmentDate);
      const dateB = new Date(b.treatmentDate);
      return dateB - dateA; // Descending order (newest first)
    });
  }, [treatmentRecords]);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '未记录';
    const date = new Date(dateString);
    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <Card variant="modern" className="border-emerald-100">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div className="flex items-center space-x-3 mb-4 sm:mb-0">
          <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center shadow-lg">
            <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              调理记录
            </h3>
            <p className="text-gray-600 text-sm">
              查看调理记录和健康进展
            </p>
          </div>
        </div>

        {canCreateRecord && (
          <Button
            onClick={() => onCreateRecord(customerId)}
            disabled={loading}
            loading={loading}
            size="small"
          >
            <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            添加记录
          </Button>
        )}
      </div>

      {/* Treatment Records List */}
      {sortedRecords.length > 0 ? (
        <div className="space-y-3">
          {sortedRecords.map((record) => (
            <Link
              key={record.id}
              to={`/treatment-records/${record.id}`}
              className="block group"
            >
              <div className="bg-white border border-gray-200 rounded-xl p-4 hover:border-purple-300 hover:shadow-lg transition-all duration-200 group-hover:bg-purple-50/30">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gradient-to-br from-purple-400 to-purple-600 rounded-lg flex items-center justify-center text-white font-medium text-sm shadow-md">
                      {record.treatmentName ? record.treatmentName.charAt(0) : 'T'}
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 group-hover:text-purple-700 transition-colors duration-200 flex items-center">
                        {record.treatmentProjectName || '常规调理'}
                        {record.followUp && (
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200">
                            <svg className="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                            随访记录
                          </span>
                        )}
                      </h4>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {formatDate(record.date)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center text-gray-600">
                    <svg className="h-4 w-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span>{record.practitionerName || '未记录调理师'}</span>
                  </div>

                  {record.venueName && (
                    <div className="flex items-center text-gray-600">
                      <svg className="h-4 w-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span>{record.venueName}</span>
                    </div>
                  )}
                </div>

                {record.summary && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                      <p className="text-sm text-gray-700 line-clamp-2">
                        {record.summary}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">无调理记录</h3>
          <p className="text-gray-500 mb-6">
            该客户尚无调理记录，开始记录第一次调理吧
          </p>
          {canCreateRecord && (
            <Button onClick={() => onCreateRecord(customerId)}>
              <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              创建首次记录
            </Button>
          )}
        </div>
      )}
    </Card>
  );
};

CustomerTreatmentRecords.propTypes = {
  customerId: PropTypes.string.isRequired,
  treatmentRecords: PropTypes.array,
  onCreateRecord: PropTypes.func.isRequired,
  loading: PropTypes.bool
};

export default CustomerTreatmentRecords;